import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { RankmiTemplateConfiguration } from '@rankmi/rankmi-templates'
import { Button, Spin } from 'antd'
import { jsPDF } from 'jspdf'
import { useNotification } from 'hooks/notifications/useNotification'
import { API } from 'config'
import { certificatesService } from 'api/administration/certificates'
import { INewCertificate } from 'interfaces/certificate'
import GeneratePdf from 'components/reusables/generatePdf'
import Title from './components/title'
import Courses from './components/courses'
import '@rankmi/rankmi-templates/dist/index.css'
import { Wrapper, Preview } from './styled'
import AssociationOfCourses from './components/associationOfCourses'
import BackgroundImage from './components/backgroundImage'
import CreatedAt from './components/createdAt'
import { TableSkeleton } from 'components/reusables/skeletons'

interface IProps {
  inMainView: boolean
}

const Templates = ({ inMainView }: IProps) => {
  const { t } = useTranslation()
  const translatePrefix = 'administration.certificate.list.create'
  const notification = useNotification()
  const [certificateId, setCertificateId] = useState(0)
  const [content, setContent] = useState('')
  const [bgImage, setBgImage] = useState<File | string | null>(null)
  const [showPreviewButton, setShowPreviewButton] = useState(false)
  const [generatePreview, setGeneratePreview] = useState(false)
  const [generating, setGenerating] = useState(false)
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    const checkElements = () => {
      const element = document.querySelector('.editable')
      setShowPreviewButton(!!element)
    }

    const observer = new MutationObserver(() => {
      checkElements()
    })

    observer.observe(document.body, { childList: true, subtree: true })

    checkElements()

    return () => {
      observer.disconnect()
    }
  }, [])

  useEffect(() => {
    if (inMainView) setBgImage(null)
  }, [inMainView])

  const overwriteColumns = [
    {
      key: 'title',
      title: t(`${translatePrefix}.table.headers.name`),
      render: (_: string, data: INewCertificate) => (
        <Title value={data.name} certificateType={data.certificate_type} />
      ),
    },
    {
      key: 'workspace_title',
      title: t(`${translatePrefix}.table.headers.createAt`),
      render: (_: number, data: INewCertificate) => (
        <CreatedAt value={data.created_at} active={data.active} />
      ),
    },
    {
      key: 'active',
      title: t(`${translatePrefix}.table.headers.courses`),
      render: (_: number, data: INewCertificate) => (
        <Courses
          data={data.certificate_course_attributes.map(
            (item: { name: string }) => item.name
          )}
        />
      ),
    },
  ]

  const nodes = {
    newTemplateHeaderNode: (
      <BackgroundImage image={bgImage} setImage={setBgImage} />
    ),
    editTemplateHeaderNode: (
      <BackgroundImage image={bgImage} setImage={setBgImage} />
    ),
  }

  const createTemplate = (
    baseProps: { resource: number },
    newTemplate: { title: string; content: string; workspace: string }
  ) => {
    return saveTemplate(
      newTemplate.title,
      newTemplate.content,
      newTemplate.workspace,
      baseProps.resource
    )
  }

  const onEditTemplate = (data: { id: number }) => {
    certificatesService.getConfiguration(data.id).then((response) => {
      setBgImage(response.data.data.attributes.backgroundImage || null)
    })
  }

  const updateTemplate = (
    baseProps: { resource: number },
    templateToken: string,
    newTemplate: { title: string; content: string; workspace: string }
  ) => {
    return saveTemplate(
      newTemplate.title,
      newTemplate.content,
      newTemplate.workspace,
      baseProps.resource,
      templateToken
    )
  }

  const saveTemplate = async (
    title: string,
    content: string,
    workspace: string,
    resource: number,
    templateToken?: string
  ) => {
    if (title.trim().length > 0 && content) {
      await certificatesService
        .saveTemplate(
          title,
          content,
          bgImage,
          workspace,
          resource,
          templateToken
        )
        .then((response) => {
          setCertificateId(response.data.data.attributes.certificateId)
          if (templateToken) {
            notification.success(
              t(`${translatePrefix}.notifications.updatedCertificate`)
            )
          } else {
            notification.success(
              t(`${translatePrefix}.notifications.newCertificateHasBeenCreated`)
            )
          }
        })
        .catch(() => {
          if (templateToken) {
            notification.warning(
              t(`${translatePrefix}.notifications.failedToUpdate`)
            )
          } else {
            notification.warning(
              t(`${translatePrefix}.notifications.failedToCreate`)
            )
          }
        })
      return { sucess: true }
    } else {
      notification.warning(
        t(`${translatePrefix}.notifications.fillInTheFields`)
      )
      return { sucess: true }
    }
  }

  const actionsCallback = {
    createTemplate: createTemplate,
    onEditTemplate: onEditTemplate,
    updateTemplate: updateTemplate,
  }

  const onGeneratePreview = () => {
    const iframe = document.querySelector(
      '.tox-edit-area__iframe'
    ) as HTMLIFrameElement
    if (iframe && iframe.contentDocument) {
      const element = iframe.contentDocument.getElementById('tinymce')
      if (element) {
        setGenerating(true)
        certificatesService
          .getPreviewContent(element.innerHTML)
          .then((response) => {
            setContent(response.data)
            setGeneratePreview(true)
          })
          .catch(() => {
            setGenerating(false)
          })
      }
    }
  }

  const onGenerate = (pdf: jsPDF | null) => {
    if (pdf) {
      const blob = pdf.output('blob')
      const url = URL.createObjectURL(blob)
      window.open(url)
    }
    setGeneratePreview(false)
    setGenerating(false)
  }

  const onDuplicate = async (token: string) => {
    setLoading(true)
    await certificatesService.duplicate(token).catch(() => setLoading(false))
    setLoading(false)
  }

  return certificateId ? (
    <AssociationOfCourses
      certificateId={certificateId}
      setCertificateId={setCertificateId}
    />
  ) : (
    <Wrapper>
      {loading ? (
        <TableSkeleton />
      ) : (
        <RankmiTemplateConfiguration
          context="lms"
          baseUrl={`//${API}`}
          resource={1}
          url={`//${API}/administration/certificates?q=%7B%22search_name_cont%22:%22%22%7D`}
          overwriteColumns={overwriteColumns}
          nodes={nodes}
          actionsCallback={actionsCallback}
          onDuplicate={onDuplicate}
        />
      )}
      {showPreviewButton && (
        <>
          <Preview>
            <Spin spinning={generating}>
              <Button onClick={onGeneratePreview}>
                {t(`${translatePrefix}.preview`)}
              </Button>
            </Spin>
          </Preview>
          {generatePreview && (
            <GeneratePdf
              content={content}
              backgroundImage={bgImage}
              onGenerate={onGenerate}
              preview={true}
            />
          )}
        </>
      )}
    </Wrapper>
  )
}

export default Templates
